import React, { useState } from "react";
import styled from "@emotion/styled";
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from "@mui/icons-material/Error";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { useChangeTargetOutcomeMutation } from "../../../../slices/api/apiSlice";
import { selectEditLeadTargetOutcomePerm } from "../../../../slices/userActionsPermissionsSlice";
import { Button } from "../../../../StyledComponents";

const ChangeTargetOutcome = ({ userId, userFullName, leadData, changeOutcomeDetails }) => {
  const theme = useTheme();

  const editLeadTargetOutcomePerm = useSelector(selectEditLeadTargetOutcomePerm)

  const [isFetching, setIsFetching] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)

  const [targetOutcome, setTargetOutcome] = useState('renegotiation')

  const [changeTargetOutcome] = useChangeTargetOutcomeMutation()

  const inferredOldOutcome = changeOutcomeDetails?.newOutcome === 'renegotiation' ? 'refinance' : 'renegotiation';
  const currentOutcome = leadData?.targetOutcome || changeOutcomeDetails?.oldOutcome || inferredOldOutcome;
  const isUnassigned = currentOutcome === 'unassigned';
  const nextOutcome = isUnassigned
    ? targetOutcome
    : currentOutcome.toUpperCase() === 'RENEGOTIATION'
      ? 'refinance'
      : 'renegotiation';
  const currentOutcomeLabel = currentOutcome === 'unassigned'
    ? 'Unassigned'
    : currentOutcome.toUpperCase() === 'RENEGOTIATION'
      ? 'Renegotiation'
      : 'Refinance';
  const nextOutcomeLabel = nextOutcome === 'renegotiation' ? 'Renegotiation' : 'Refinance';

  const handleChangeTargetOutcome = (unassigned) => {
    setIsFetching(true)
    setIsSuccess(false)
    setIsError(false)
    let newOutcome = ''
    if (!leadData && changeOutcomeDetails) {
      newOutcome = changeOutcomeDetails.newOutcome
    } else if (unassigned) {
      newOutcome = targetOutcome
    } else if (leadData.targetOutcome.toUpperCase() === 'RENEGOTIATION') {
      newOutcome = 'refinance'
    } else {
      newOutcome = 'renegotiation'
    }
    let resp = changeTargetOutcome({
      userId: userId,
      timeline: leadData?.timeline || changeOutcomeDetails?.timeline,
      userFullName: userFullName,
      newOutcome: newOutcome,
      oldOutcome: leadData?.targetOutcome || changeOutcomeDetails?.oldOutcome || inferredOldOutcome,
      leadId: leadData?._id || changeOutcomeDetails?.leadId,
    })
    if (resp.error) {
      setIsError(true)
    } else {
      setIsSuccess(true)
    }
    setIsFetching(false)
  }

  const handleTargetOutcome = (event) => {
    setTargetOutcome(event.target.value)
  }

  return (
      <OutcomePanel>
        {isSuccess ?
          <PanelHeader $tone="success">
            <HeaderIcon>
              <CheckIcon />
            </HeaderIcon>
            <HeaderText>Success</HeaderText>
          </PanelHeader>
        : isError ?
          <PanelHeader $tone="error">
            <HeaderIcon>
              <ErrorIcon />
            </HeaderIcon>
            <HeaderText>
              Failed... please try again. Reach out to support@lancastersweep.com if the problem persists.
            </HeaderText>
          </PanelHeader>
        : isUnassigned ?
          <PanelHeader>
            <HeaderText>Assign Target Outcome</HeaderText>
            <HeaderMeta>Select the intended path for this investigation.</HeaderMeta>
          </PanelHeader>
        :
          <PanelHeader>
            <HeaderText>Change Target Outcome</HeaderText>
            <HeaderMeta>Review the current path before confirming the change.</HeaderMeta>
          </PanelHeader>
        }

        <PanelBody>
          <DecisionSurface>
            <MessageBlock>
              <MessageTitle>
                {isSuccess ? 'Outcome updated' : isUnassigned ? 'Outcome required' : 'Confirm outcome change'}
              </MessageTitle>
              <MessageCopy>
              {isSuccess ?
                `The investigation outcome has been set to a ${currentOutcome.toUpperCase() === 'RENEGOTIATION' ? 'renegotiation' : 'refinance'}.`
              : isUnassigned ?
                `Select the intended outcome of this investigation.`
              :
                `Change the target outcome of this investigation to a ${currentOutcome.toUpperCase() === 'RENEGOTIATION' ? 'refinance' : 'renegotiation'}?`
              }
              </MessageCopy>
            </MessageBlock>

            {isUnassigned ?
              <OutcomeOptions role="radiogroup" aria-label="Target outcome">
                <OutcomeOption
                  type="button"
                  value="renegotiation"
                  role="radio"
                  aria-checked={targetOutcome === "renegotiation"}
                  className={targetOutcome === "renegotiation" ? "selected" : ""}
                  onClick={handleTargetOutcome}
                >
                  <OutcomeLabel>Renegotiation</OutcomeLabel>
                  <OutcomeDescription>Use when the investigation is expected to resolve through changed loan terms.</OutcomeDescription>
                </OutcomeOption>
                <OutcomeOption
                  type="button"
                  value="refinance"
                  role="radio"
                  aria-checked={targetOutcome === "refinance"}
                  className={targetOutcome === "refinance" ? "selected" : ""}
                  onClick={handleTargetOutcome}
                >
                  <OutcomeLabel>Refinance</OutcomeLabel>
                  <OutcomeDescription>Use when the investigation is expected to resolve through a replacement loan.</OutcomeDescription>
                </OutcomeOption>
              </OutcomeOptions>
            :
              <OutcomeComparison aria-label="Outcome change summary">
                <OutcomeCard>
                  <OutcomeKicker>Current outcome</OutcomeKicker>
                  <OutcomeValue>{currentOutcomeLabel}</OutcomeValue>
                </OutcomeCard>
                <OutcomeArrow>to</OutcomeArrow>
                <OutcomeCard className="next">
                  <OutcomeKicker>New outcome</OutcomeKicker>
                  <OutcomeValue>{nextOutcomeLabel}</OutcomeValue>
                </OutcomeCard>
              </OutcomeComparison>
            }
          </DecisionSurface>
        </PanelBody>

        {(!isSuccess && !isError) &&
          <PanelFooter>
              {isFetching ?
                <Button className="primaryLoading">
                  <CircularProgress
                    sx={{ color: `${theme.palette.common.cardBackground}` }}
                    size={13}
                  />
                </Button>
              :
                <Button onClick={editLeadTargetOutcomePerm && currentOutcome !== 'unassigned' ? () => handleChangeTargetOutcome(false) : editLeadTargetOutcomePerm && currentOutcome === 'unassigned' ? () => handleChangeTargetOutcome('unassigned') : null} className={editLeadTargetOutcomePerm ? "primary" : "primaryDisabled"}>
                  Confirm
                </Button>
              }         
          </PanelFooter>
        }
      </OutcomePanel>
  )
}

const OutcomePanel = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  background: ${theme.palette.common.cardBackground};
  overflow: hidden;
`)

const PanelHeader = styled.header(({ theme, $tone }) => {
  const isSuccess = $tone === "success";
  const isError = $tone === "error";
  const toneColor = isSuccess
    ? theme.palette.primary.dark
    : isError
      ? theme.palette.common.redDark
      : theme.palette.common.grayText;
  const toneBackground = isSuccess
    ? theme.palette.primary.labelTransparent
    : isError
      ? theme.palette.common.redTransparent
      : theme.palette.common.white;

  return `
    min-height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    padding: 0.8rem 1rem;
    border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
    background: ${toneBackground};
    color: ${toneColor};
  `;
})

const HeaderIcon = styled.div(({ theme }) => `
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.6rem;
  background: ${theme.palette.common.white};
  border: 0.1rem solid currentColor;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`)

const HeaderText = styled.span`
  color: inherit;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
  font-family: -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", 'Inter', "Helvetica Neue", sans-serif;
`

const HeaderMeta = styled.span(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.9rem;
  font-weight: 600;
`)

const PanelBody = styled.div(({ theme }) => `
  min-height: 0;
  display: grid;
  padding: 1rem;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${theme.palette.common.grayBorderLight};

  ::-webkit-scrollbar-thumb {
    border: 0.1rem solid ${theme.palette.common.grayText};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    border: 0.05rem solid ${theme.palette.common.grayBorderDark};
    background-color: ${theme.palette.common.pageBackground};
  }

  ::-webkit-scrollbar:vertical {
    width: 0.4rem;
  }
`)

const DecisionSurface = styled.div(({ theme }) => `
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  padding: 2rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
`)

const MessageBlock = styled.div`
  display: grid;
  gap: 0.45rem;
  max-width: 40rem;
  text-align: center;
`

const MessageTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 1.35rem;
  font-weight: 800;
`)

const MessageCopy = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.45;
`)

const OutcomeOptions = styled.div`
  width: min(100%, 42rem);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
`

const OutcomeOption = styled.button(({ theme }) => `
  min-height: 8rem;
  display: grid;
  gap: 0.45rem;
  align-content: center;
  padding: 1.1rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.cardBackground};
  color: ${theme.palette.common.black};
  text-align: left;
  cursor: pointer;

  :hover {
    border-color: ${theme.palette.primary.medium};
    background: ${theme.palette.common.white};
  }

  &.selected {
    border-color: ${theme.palette.primary.dark};
    border-left: 0.35rem solid ${theme.palette.primary.dark};
    background: ${theme.palette.primary.labelTransparent};
  }
`)

const OutcomeLabel = styled.span`
  font-size: 1rem;
  font-weight: 800;
  text-transform: uppercase;
`

const OutcomeDescription = styled.span(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.4;
`)

const OutcomeComparison = styled.div`
  width: min(100%, 42rem);
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: stretch;
  gap: 0.8rem;
`

const OutcomeCard = styled.div(({ theme }) => `
  display: grid;
  align-content: center;
  gap: 0.4rem;
  min-height: 7rem;
  padding: 1rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.cardBackground};
  text-align: center;

  &.next {
    border-color: ${theme.palette.primary.dark};
    border-left: 0.35rem solid ${theme.palette.primary.dark};
    background: ${theme.palette.primary.labelTransparent};
  }
`)

const OutcomeKicker = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
`)

const OutcomeValue = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 1.15rem;
  font-weight: 800;
`)

const OutcomeArrow = styled.div(({ theme }) => `
  align-self: center;
  color: ${theme.palette.common.grayText};
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
`)

const PanelFooter = styled.footer(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1rem;
  border-top: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`)

export default ChangeTargetOutcome;
